import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, ArrowUpRight, Building2, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { keycloakLogin, isKeycloakEnabled } from '../services/keycloak';
import { Footer } from '../components/Footer';
import { InstagramIcon, LinkedInIcon } from '../components/ui/BrandIcons';
import { ASSOCIATED_PARTNERS, BENEFICIARIES } from '../config/partners';
import '../styles/Login.css';

const PROJECT_URL = 'https://www.livebetterproject.eu/';

const FEATURES = [
  {
    icon: Building2,
    title: 'Building intelligence',
    text: 'Monitor your buildings and use AI-based services for energy, maintenance and renovation planning.',
  },
  {
    icon: ArrowLeftRight,
    title: 'Data marketplace',
    text: 'Exchange building data for access to services, with agreements secured by smart contracts.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure and GDPR-aligned',
    text: 'Single sign-on, role-based access and GDPR-aligned data protection across the Hub.',
  },
];

const COUNTRY_COUNT = new Set([...BENEFICIARIES, ...ASSOCIATED_PARTNERS].map((p) => p.country)).size;

const signIn = () => keycloakLogin(window.location.origin + '/dashboard');

const PartnerCard = ({ partner }) => {
  const content = (
    <>
      {/* Decorative: the short and full name below already name the partner. */}
      <span className="partner-logo">
        <img src={partner.logo} alt="" loading="lazy" />
      </span>
      <span className="partner-head">
        <span className="partner-short">{partner.shortName}</span>
        {partner.coordinator && <span className="partner-badge">Coordinator</span>}
      </span>
      <span className="partner-name" title={partner.name}>
        {partner.name}
      </span>
      <span className="partner-country">{partner.country}</span>
    </>
  );

  if (!partner.url) {
    return <div className="partner-card">{content}</div>;
  }
  return (
    <a className="partner-card" href={partner.url} target="_blank" rel="noopener noreferrer">
      {content}
      <ArrowUpRight className="partner-link-icon" size={16} aria-hidden="true" />
    </a>
  );
};

const PartnerGrid = ({ partners }) => (
  <ul className="partner-grid">
    {partners.map((partner) => (
      <li key={partner.shortName}>
        <PartnerCard partner={partner} />
      </li>
    ))}
  </ul>
);

// Public landing page and SSO entry point. Accounts are created by an
// administrator in Keycloak, and sign-in, password reset and password
// changes all happen there.
export const Login = () => {
  const navigate = useNavigate();
  const { user, authError, signOut } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  return (
    <div className="login-page">
      <header className="landing-nav">
        <div className="landing-container landing-nav-inner">
          <img src="/livebetter_wordmark.png" alt="LiveBetter" className="landing-logo" />
          <nav className="landing-nav-links" aria-label="Main">
            <a href={PROJECT_URL}>About the project</a>
            <a href="#partners">Partners</a>
            <a href="#contact">Contact</a>
          </nav>
          {isKeycloakEnabled && (
            <button type="button" className="app-btn app-btn--primary" onClick={signIn}>
              Sign in
            </button>
          )}
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <img src="/cta-figure.svg" alt="" aria-hidden="true" className="landing-hero-figure" />
          <div className="landing-container landing-hero-inner">
            <div>
              <p className="landing-eyebrow">LiveBetter Hub</p>
              <h1>
                Better data.
                <br />
                Smarter buildings.
                <br />
                Real renovation.
              </h1>
              <p className="landing-lead">
                The LiveBetter Hub connects high-quality building data with AI-based services that help plan and
                track deep renovation, in one secure platform.
              </p>

              {!isKeycloakEnabled && (
                <div className="error-message" role="alert">
                  Single sign-on is not configured for this environment. Please contact your administrator.
                </div>
              )}
              {authError && (
                <div className="error-message" role="alert">
                  You signed in, but the Hub could not open your session ({authError}). Please try again, or
                  contact your administrator if it keeps happening.{' '}
                  <button type="button" className="app-btn app-btn--secondary" onClick={signOut}>
                    Sign out
                  </button>
                </div>
              )}
              <div className="landing-actions">
                {isKeycloakEnabled && (
                  <button type="button" className="app-btn app-btn--primary landing-cta" onClick={signIn}>
                    Sign in
                  </button>
                )}
                <a className="app-btn app-btn--secondary landing-cta" href={PROJECT_URL}>
                  Learn more
                </a>
              </div>
              <p className="landing-hint">Accounts are created by your LiveBetter administrator.</p>
            </div>

            <ul className="landing-features">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <li key={title} className="landing-feature">
                  <span className="landing-feature-icon" aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="partners" className="landing-section landing-partners">
          <div className="landing-container">
            <p className="landing-eyebrow">Consortium</p>
            <h2>Partners</h2>
            <p className="landing-section-lead">
              LiveBetter brings together {BENEFICIARIES.length} beneficiaries and {ASSOCIATED_PARTNERS.length}{' '}
              associated partners from {COUNTRY_COUNT} European countries, coordinated by ICCS.
            </p>

            <h3 className="partner-group-title">Beneficiaries</h3>
            <PartnerGrid partners={BENEFICIARIES} />

            <h3 className="partner-group-title">Associated partners</h3>
            <PartnerGrid partners={ASSOCIATED_PARTNERS} />
          </div>
        </section>

        <section id="contact" className="landing-section">
          <div className="landing-container landing-contact">
            <div>
              <h2>Contact</h2>
              <p className="landing-section-lead">
                Questions about the Hub or the LiveBetter project? Get in touch with the consortium.
              </p>
            </div>
            <ul className="landing-contact-links">
              <li>
                <a className="app-btn app-btn--secondary" href="mailto:contact@livebetter.eu">
                  <Mail size={16} aria-hidden="true" />
                  contact@livebetter.eu
                </a>
              </li>
              <li>
                <a
                  className="app-btn app-btn--secondary"
                  href="https://www.linkedin.com/company/livebetter-project/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon size={16} aria-hidden="true" />
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  className="app-btn app-btn--secondary"
                  href="https://www.instagram.com/livebetter.project.eu/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon size={16} aria-hidden="true" />
                  Instagram
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>

      <Footer containerClassName="max-w-[1200px] px-4 sm:px-6" />
    </div>
  );
};
